/**
 * Data layer for `campaign-dashboard` (feature #62).
 *
 * Thin typed fetch wrappers over `/api/v1/campaign-dashboard` — the backend
 * exposes a read-only surface (list + stats) with keyset pagination, so there
 * is NO create/update/delete here. Raw `fetch()` is confined to this module
 * per the dash contract (rule D6).
 *
 * HTTP contract (mirrors apps/api/src/routes/campaign-dashboard.ts):
 *   GET /api/v1/campaign-dashboard/list   ?state=&vertical=&q=&limit=&cursor=
 *     -> { items: CampaignRow[], next_cursor: string | null }
 *   GET /api/v1/campaign-dashboard/stats
 *     -> { tenant_id, by_state, totals }
 *
 * The page renders the list as a campaign table with per-row status pills,
 * a state filter, a vertical filter, a substring search, and row actions
 * (view details / copy ID). Totals/stats power the header KPI strip.
 */

// ---------------------------------------------------------------------------
// Types — mirrors backend row + rollup shape.
// ---------------------------------------------------------------------------

export const CAMPAIGN_STATES = ["draft", "active", "paused", "archived"] as const;
export type CampaignState = (typeof CAMPAIGN_STATES)[number];

export interface CampaignRow {
	id: string;
	tenant_id: string;
	name: string;
	vertical: string;
	state: CampaignState;
	clicks_count: number;
	conversions_count: number;
	revenue_minor: number;
	currency: string;
	updated_at: number;
}

export interface ListCampaignsResponse {
	items: CampaignRow[];
	next_cursor: string | null;
}

export interface StatsResponse {
	tenant_id: string;
	by_state: Record<CampaignState, { count: number; revenue_minor: number }>;
	totals: {
		count: number;
		clicks: number;
		conversions: number;
		revenue_minor: number;
		currency: string;
	};
}

export interface ListFilters {
	state?: CampaignState;
	vertical?: string;
	q?: string;
	limit?: number;
	cursor?: string;
}

// ---------------------------------------------------------------------------
// Client-side validators — mirrors server allowlist to surface feedback fast.
// ---------------------------------------------------------------------------

export function isValidCampaignState(v: unknown): v is CampaignState {
	return (
		typeof v === "string" &&
		(CAMPAIGN_STATES as readonly string[]).includes(v)
	);
}

export function clampLimit(n: number, fallback = 25): number {
	if (!Number.isFinite(n) || !Number.isInteger(n)) return fallback;
	if (n < 1) return 1;
	if (n > 100) return 100;
	return n;
}

// ---------------------------------------------------------------------------
// URL helpers (pure, exported for tests).
// ---------------------------------------------------------------------------

export function buildListUrl(base: string, filters: ListFilters = {}): string {
	const params = new URLSearchParams();
	if (filters.state !== undefined && filters.state !== null) {
		params.set("state", filters.state);
	}
	if (filters.vertical !== undefined && filters.vertical.length > 0) {
		params.set("vertical", filters.vertical);
	}
	if (filters.q !== undefined && filters.q.length > 0) {
		params.set("q", filters.q);
	}
	if (filters.limit !== undefined) {
		params.set("limit", String(clampLimit(filters.limit)));
	}
	if (filters.cursor !== undefined && filters.cursor.length > 0) {
		params.set("cursor", filters.cursor);
	}
	const qs = params.toString();
	return qs.length > 0 ? `${base}/list?${qs}` : `${base}/list`;
}

// ---------------------------------------------------------------------------
// Response parser — normalizes the shared `{error}` envelope into thrown
// Error instances so TanStack Query picks them up.
// ---------------------------------------------------------------------------

const BASE = "/api/v1/campaign-dashboard";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

// ---------------------------------------------------------------------------
// Remote bindings.
// ---------------------------------------------------------------------------

export async function listCampaigns(
	filters: ListFilters = {},
): Promise<ListCampaignsResponse> {
	const res = await fetch(buildListUrl(BASE, filters), {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCampaignsResponse>(res);
}

export async function fetchStats(): Promise<StatsResponse> {
	const res = await fetch(`${BASE}/stats`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<StatsResponse>(res);
}

// ---------------------------------------------------------------------------
// Query-key factory — stable identities for TanStack Query invalidation.
// ---------------------------------------------------------------------------

export const campaignDashboardQueryKeys = {
	all: ["campaign-dashboard"] as const,
	list: (filters: ListFilters = {}) =>
		[
			"campaign-dashboard",
			"list",
			{
				state: filters.state ?? null,
				vertical: filters.vertical ?? null,
				q: filters.q ?? null,
				limit: filters.limit ?? null,
				cursor: filters.cursor ?? null,
			},
		] as const,
	stats: () => ["campaign-dashboard", "stats"] as const,
};

// ---------------------------------------------------------------------------
// Presentation helpers (pure, exported for tests).
// ---------------------------------------------------------------------------

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return Math.trunc(n).toLocaleString("en-US");
}

export function formatRevenue(minor: number, currency: string): string {
	if (!Number.isFinite(minor)) minor = 0;
	const amount = (minor / 100).toFixed(2);
	const withThousands = amount.replace(
		/\B(?=(\d{3})+(?!\d))/g,
		",",
	);
	const code = currency && currency.length > 0 ? currency : "USD";
	return `${withThousands} ${code.toUpperCase()}`;
}

export function formatUpdatedAt(ts: number): string {
	if (!Number.isFinite(ts) || ts <= 0) return "";
	const ms = ts < 1e12 ? ts * 1000 : ts;
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return "";
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	const hh = String(d.getHours()).padStart(2, "0");
	const mm = String(d.getMinutes()).padStart(2, "0");
	return `${year}-${month}-${day} ${hh}:${mm}`;
}

export interface StatePillTheme {
	label: string;
	className: string;
}

// Emerald → sky spectrum only. Light bg + zinc-bordered pill.
export function statePillTheme(state: CampaignState): StatePillTheme {
	switch (state) {
		case "active":
			return {
				label: "Live",
				className:
					"bg-emerald-50 text-emerald-700 border border-emerald-200",
			};
		case "paused":
			return {
				label: "Paused",
				className:
					"bg-sky-50 text-sky-700 border border-sky-200",
			};
		case "draft":
			return {
				label: "Draft",
				className:
					"bg-zinc-50 text-zinc-700 border border-zinc-200",
			};
		case "archived":
			return {
				label: "Archived",
				className:
					"bg-zinc-100 text-zinc-500 border border-zinc-200",
			};
	}
}

export function computeFilteredCount(
	items: readonly CampaignRow[],
	state?: CampaignState,
	vertical?: string,
): number {
	return items.filter((r) => {
		if (state !== undefined && r.state !== state) return false;
		if (vertical !== undefined && vertical.length > 0 && r.vertical !== vertical) {
			return false;
		}
		return true;
	}).length;
}

export function uniqueVerticals(items: readonly CampaignRow[]): string[] {
	const seen = new Set<string>();
	const out: string[] = [];
	for (const r of items) {
		if (r.vertical && !seen.has(r.vertical)) {
			seen.add(r.vertical);
			out.push(r.vertical);
		}
	}
	return out.sort((a, b) => a.localeCompare(b));
}
